/**
 * Shared utility functions for managing chat context in cloud map components.
 * These utilities help clear various types of context when navigating between maps
 * or focusing on specific entities.
 */

/**
 * Clears all entity_focus contexts from the journeys chat context.
 * Used when switching focus between different entities or maps.
 *
 * @param {Object} $journeys - The journeys store containing chat context
 */
export function clearAllEntityFocusContexts($journeys) {
  if (!$journeys) {
    return;
  }

  // Get all current contexts and remove any with type 'entity_focus'
  if ($journeys.activeChatContext && typeof $journeys.activeChatContext.forEach === 'function') {
    const entityFocusContexts = [];
    $journeys.activeChatContext.forEach((context) => {
      if (context.type === 'entity_focus') {
        entityFocusContexts.push(context);
      }
    });

    // Remove all found entity_focus contexts
    entityFocusContexts.forEach((context) => {
      $journeys.removeChatContext({
        type: context.type,
        name: context.name,
        context: 'Previous entity focus context'
      });
    });
  }
}

/**
 * Clears redundant cloud provider contexts from the overview map.
 * These contexts become redundant when viewing CSP-specific topology.
 *
 * @param {Object} $journeys - The journeys store containing chat context
 */
export function clearRedundantCloudProviderContext($journeys) {
  if (!$journeys) {
    return;
  }

  // Remove generic cloud provider contexts from overview map that become redundant
  // when viewing CSP-specific topology
  if ($journeys.activeChatContext && typeof $journeys.activeChatContext.forEach === 'function') {
    const cloudProviderContextsToRemove = [];
    $journeys.activeChatContext.forEach((context) => {
      // Remove cloud provider contexts from overview map selection
      if (
        context.type === 'topology_loaded' &&
        (context.name === 'Cloud AWS' ||
          context.name === 'Cloud Azure' ||
          context.name === 'Cloud GCP' ||
          context.name === 'Cloud OCI')
      ) {
        cloudProviderContextsToRemove.push(context);
      }
      // Also remove entity_focus contexts for cloud providers
      if (
        context.type === 'entity_focus' &&
        context.name &&
        (context.name.toLowerCase().includes('cloud aws') ||
          context.name.toLowerCase().includes('cloud azure') ||
          context.name.toLowerCase().includes('cloud gcp') ||
          context.name.toLowerCase().includes('cloud oci'))
      ) {
        cloudProviderContextsToRemove.push(context);
      }
    });

    // Remove all found cloud provider contexts
    cloudProviderContextsToRemove.forEach((context) => {
      $journeys.removeChatContext({
        type: context.type,
        name: context.name,
        context: 'Previous cloud provider context from overview map'
      });
    });
  }
}

/**
 * Clears general topology overview contexts for a specific cloud provider.
 * Used when user focuses on specific entities within a cloud provider map.
 *
 * @param {Object} $journeys - The journeys store containing chat context
 * @param {string} cloudProvider - The cloud provider name (e.g., 'AWS', 'Azure', 'GCP', 'OCI')
 */
export function clearGeneralTopologyContext($journeys, cloudProvider) {
  if (!$journeys) {
    return;
  }

  // Remove all general topology overview contexts when user focuses on specific entities
  const generalContextsToRemove = [
    { type: 'topology_overview', name: 'On Prem Routers' },
    { type: 'topology_overview', name: 'On Prem Topology' },
    { type: 'topology_overview', name: `${cloudProvider} Regions` },
    { type: 'topology_overview', name: `${cloudProvider} Topology` }
  ];

  generalContextsToRemove.forEach((context) => {
    $journeys.removeChatContext({
      ...context,
      context: 'General topology context'
    });
  });
}

/**
 * Clears all CSP-specific contexts when leaving a cloud provider map.
 * This includes topology_loaded, entity_focus, and topology_overview contexts
 * specific to the given cloud provider.
 *
 * @param {Object} $journeys - The journeys store containing chat context
 * @param {Object} config - Configuration object
 * @param {string} config.cloudProvider - The cloud provider name (lowercase: 'aws', 'azure', 'gcp', 'oci')
 * @param {string} config.cloudProviderDisplay - The display name (e.g., 'AWS', 'Azure', 'GCP', 'OCI')
 * @param {Array<string>} [config.additionalKeywords] - Additional keywords to match in context names (e.g., ['vnet'] for Azure, ['vcn'] for OCI)
 */
export function clearCSPSpecificContexts($journeys, config) {
  if (!$journeys) {
    return;
  }

  const { cloudProvider, cloudProviderDisplay, additionalKeywords = [] } = config;

  // Clear all CSP-specific contexts when leaving the map
  if ($journeys.activeChatContext && typeof $journeys.activeChatContext.forEach === 'function') {
    const cspSpecificContexts = [];
    $journeys.activeChatContext.forEach((context) => {
      // Remove any context that is specific to this CSP map
      const isTopologyLoaded =
        context.type === 'topology_loaded' &&
        (context.name === `Current ${cloudProviderDisplay} Topology` ||
          context.name === `Cloud ${cloudProviderDisplay}` ||
          context.name === 'On Prem Routers' ||
          context.name === 'On Prem Topology' ||
          context.name === `${cloudProviderDisplay} Regions` ||
          context.name === `${cloudProviderDisplay} Topology`);

      // Check if context name matches cloud provider or additional keywords
      const matchesCloudProvider = context.name && context.name.toLowerCase().includes(cloudProvider.toLowerCase());
      const matchesAdditionalKeywords =
        context.name &&
        additionalKeywords.some((keyword) => context.name.toLowerCase().includes(keyword.toLowerCase()));

      const isEntityFocus =
        context.type === 'entity_focus' &&
        (context.cloudProvider === cloudProvider ||
          context.model?.cloudProvider === cloudProvider ||
          matchesCloudProvider ||
          matchesAdditionalKeywords);

      const isTopologyOverview = context.type === 'topology_overview' && context.model?.cloudProvider === cloudProvider;

      if (isTopologyLoaded || isEntityFocus || isTopologyOverview) {
        cspSpecificContexts.push(context);
      }
    });

    // Remove all found CSP-specific contexts
    cspSpecificContexts.forEach((context) => {
      $journeys.removeChatContext({
        type: context.type,
        name: context.name,
        context: 'Clearing CSP-specific context on navigation'
      });
    });
  }
}
